import * as React from "react";

import { cn } from "@garther/ui/libs";
import { Button } from "../Button";
import { Minus, Plus } from "lucide-react";

export * from "./select";
export interface QuantityInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  label?: string;
  className?: string;
  range: {
    min: number;
    max: number;
  };
  step: number;
  quantity: number;
  handleQuantityChange: (delta: number) => void;
  error?: string;
  disabled?: boolean;
  allowDirectInput?: boolean;
}

export const QuantityInput = React.forwardRef<
  HTMLInputElement,
  QuantityInputProps
>(
  (
    {
      className,
      range,
      step,
      quantity,
      handleQuantityChange,
      error,
      disabled = false,
      allowDirectInput = false,
      label,
      ...props
    },
    ref
  ) => {
    const handleDirectInputChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const value = parseInt(e.target.value, 10);
      if (!isNaN(value) && value >= range.min && value <= range.max) {
        const delta = value - quantity;
        handleQuantityChange(delta);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        handleQuantityChange(step);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        handleQuantityChange(-step);
      }
    };

    return (
      <div className={cn("space-y-2", className)}>
        {label && (
          <label className="text-text-secondary font-medium text-responsive-sm">
            {label}
          </label>
        )}
        <div
          className="flex items-center gap-3"
          onKeyDown={handleKeyDown}
          role="group"
          aria-label={`Quantity selector, current value: ${quantity}`}
        >
          <Button
            variant="outline"
            padding="p-0"
            className="h-6 w-6 rounded-sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleQuantityChange(-step);
            }}
            disabled={disabled || quantity <= range.min}
            aria-label={`Decrease quantity by ${step}`}
          >
            <Minus className="h-4 w-4" />
          </Button>

          {allowDirectInput ? (
            <input
              ref={ref}
              type="number"
              min={range.min}
              max={range.max}
              step={step}
              value={quantity}
              onChange={handleDirectInputChange}
              className="w-12 text-center font-normal text-responsive-md border border-border-light rounded-sm px-1 py-0.5 focus:outline-none focus:border-border-dark"
              disabled={disabled}
              aria-label="Quantity input"
              {...props}
            />
          ) : (
            <span
              className="w-8 text-center font-normal text-responsive-md"
              aria-live="polite"
            >
              {quantity}
            </span>
          )}

          <Button
            variant="outline"
            padding="p-0"
            className="h-6 w-6 rounded-sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleQuantityChange(step);
            }}
            disabled={disabled || quantity >= range.max}
            aria-label={`Increase quantity by ${step}`}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
      </div>
    );
  }
);
QuantityInput.displayName = "QuantityInput";
